/** Browser geolocation helpers for field punches, job start/complete, and photo metadata. */

export type GeoPosition = {
  latitude: number
  longitude: number
  accuracy: number | null
  capturedAt: string
}

export type GeoErrorCode = 'unsupported' | 'denied' | 'unavailable' | 'timeout'

export type GeoCaptureResult = {
  position: GeoPosition | null
  error: GeoErrorCode | null
}

function mapGeoError(code: number | undefined): GeoErrorCode {
  if (code === 1) return 'denied'
  if (code === 3) return 'timeout'
  return 'unavailable'
}

/**
 * Raw GPS capture. Prefer `requireGpsCapture` / `gateJobSiteAction` from
 * `fieldGpsGate` for field actions — those require a fix and enforce geofencing.
 */
export function captureGeolocation(): Promise<GeoCaptureResult> {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
      resolve({ position: null, error: 'unsupported' })
      return
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          position: {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy:
              typeof pos.coords.accuracy === 'number' && Number.isFinite(pos.coords.accuracy)
                ? pos.coords.accuracy
                : null,
            capturedAt: new Date(pos.timestamp || Date.now()).toISOString(),
          },
          error: null,
        }),
      (err) => resolve({ position: null, error: mapGeoError(err?.code) }),
      // Fresh fix preferred — stale/cached coarse Android positions were
      // previously accepted for up to 60s and could bypass site checks.
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    )
  })
}

/** @deprecated GPS is now required — kept for any remaining call sites. */
export function warnIfGpsDenied(result: GeoCaptureResult, actionLabel: string): void {
  if (result.position) return
  alert(`${actionLabel} requires GPS. Enable location access and try again.`)
}

export function jobStartGpsPatch(position: GeoPosition | null): Record<string, unknown> {
  if (!position) return {}
  return {
    start_latitude: position.latitude,
    start_longitude: position.longitude,
    start_accuracy_m: position.accuracy,
  }
}

export function jobCompleteGpsPatch(position: GeoPosition | null): Record<string, unknown> {
  if (!position) return {}
  return {
    complete_latitude: position.latitude,
    complete_longitude: position.longitude,
    complete_accuracy_m: position.accuracy,
  }
}
